import { Injectable } from '@angular/core';
import { UploadedFile } from '../../../lib/ni-file-upload/ni-file-upload.component';

const STORAGE_KEY = 'formBuilder_data';
const STORAGE_KEY_CONFIG = 'formBuilder_config';
const STORAGE_KEY_UPLOADED_FILES = 'formBuilder_uploadedFiles';

@Injectable({
  providedIn: 'root'
})
export class FormBuilderStorageService {
  
  /**
   * Save form values to sessionStorage (simple)
   */
  saveFormValues(formValues: Record<string, any>): void {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(formValues));
    } catch (error) {
      
    }
  }

  /**
   * Load form values from sessionStorage (simple)
   */
  loadFormValues(programId?: string): Record<string, any> | null {
    try {
      // If programId changed, clear old data
      if (programId) {
        const savedProgramId = this.loadProgramId();
        if (savedProgramId && savedProgramId !== programId) {
          this.clearAll();
          return null;
        }
      }
      
      const data = sessionStorage.getItem(STORAGE_KEY);
      if (data) {
        return JSON.parse(data);
      }
    } catch (error) {
      
    }
    return null;
  }

  /**
   * Save current section ID to sessionStorage
   */
  saveCurrentSection(sectionId: string): void {
    try {
      sessionStorage.setItem('formBuilder_currentSection', sectionId);
    } catch (error) {
      
    }
  }

  /**
   * Load current section ID from sessionStorage
   */
  loadCurrentSection(): string | null {
    try {
      return sessionStorage.getItem('formBuilder_currentSection');
    } catch (error) {
      
      return null;
    }
  }

  /**
   * Save program ID to sessionStorage (to know which form to restore)
   */
  saveProgramId(programId: string): void {
    try {
      sessionStorage.setItem('formBuilder_programId', programId);
    } catch (error) {
      
    }
  }

  /**
   * Load program ID from sessionStorage
   */
  loadProgramId(): string | null {
    try {
      return sessionStorage.getItem('formBuilder_programId');
    } catch (error) {
      
      return null;
    }
  }

  /**
   * Clear all persisted form data
   */
  clearAll(): void {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
      sessionStorage.removeItem(STORAGE_KEY_CONFIG);
      sessionStorage.removeItem(STORAGE_KEY_UPLOADED_FILES);
      sessionStorage.removeItem('formBuilder_currentSection');
      sessionStorage.removeItem('formBuilder_programId');
      
    } catch (error) {
      
    }
  }

  /**
   * Check if there's persisted data
   */
  hasPersistedData(): boolean {
    return sessionStorage.getItem(STORAGE_KEY) !== null;
  }

  /**
   * Save uploaded files map to sessionStorage
   */
  saveUploadedFilesMap(uploadedFilesMap: Record<string, UploadedFile[]>): void {
    try {
      // Convert UploadedFile objects to a serializable format
      // Note: File objects cannot be serialized, so we store metadata only
      const serializableMap: Record<string, any[]> = {};
      Object.keys(uploadedFilesMap).forEach(key => {
        serializableMap[key] = uploadedFilesMap[key].map(file => ({
          key: file.key,
          fileName: file.file.name,
          fileSize: file.file.size,
          fileType: file.file.type,
          uploading: file.uploading ?? false,
          error: file.error ?? null
        }));
      });
      sessionStorage.setItem(STORAGE_KEY_UPLOADED_FILES, JSON.stringify(serializableMap));
    } catch (error) {
      
    }
  }

  /**
   * Load uploaded files map from sessionStorage
   * Note: This returns metadata only, actual File objects need to be reconstructed
   */
  loadUploadedFilesMap(): Record<string, any[]> | null {
    try {
      const data = sessionStorage.getItem(STORAGE_KEY_UPLOADED_FILES);
      if (data) {
        return JSON.parse(data);
      }
    } catch (error) {
      
    }
    return null;
  }

  /**
   * Clear uploaded files map from sessionStorage
   */
  clearUploadedFilesMap(): void {
    try {
      sessionStorage.removeItem(STORAGE_KEY_UPLOADED_FILES);
    } catch (error) {
      
    }
  }
}

